import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { createProxySignal, date } from 'common';
import { requireCalendarContext } from '../st-schedule-grid';
import { Dorito, ToolButton } from '../../..';
import MonthGrid from './MonthGrid';
import styles from '../st-schedule-grid.module.css';

const { startOfWeek, addDays, addMonths, startOfMonth, endOfMonth, endOfWeek, truncateTime } = date;

export function MiniNavigation() {
    const ctx = requireCalendarContext();

    return <div class={`${styles['mini-calendar-nav']} flexbox horizontal middle`}>
        <ToolButton icon="fa fa-chevron-left" label="Previous Month" onClick={() => ctx.setDay(addMonths(ctx.day(), -1))} />
        <MonthGrid />
        <ToolButton icon="fa fa-chevron-right" label="Next Month" onClick={() => ctx.setDay(addMonths(ctx.day(), 1))} />
    </div>
}

export function MiniCalendar() {
    const ctx = requireCalendarContext();

    return <div class={styles['mini-calendar']}>
        <For each={ctx.weeks()}>{(w) => <Week date={w} />}</For>
        <SelectedDaysIndicator />
    </div>
}

function SelectedDaysIndicator() {
    const MS_PER_DAY = (1000 * 3600 * 24);
    const ctx = requireCalendarContext();

    const [cellHeight, setCellHeight] = createSignal(30);
    const [cellWidth, setCellWidth] = createSignal(20);
    function initElement(el: HTMLDivElement)
    {
        const observer = new ResizeObserver((entries) => {
            requestAnimationFrame(() => {
                if (el.parentElement) {
                    const dt = ctx.day();
                    const thisMonth = startOfWeek(startOfMonth(dt));
                    const nextMonth = endOfWeek(endOfMonth(dt));
                    const days = (nextMonth.getTime() - thisMonth.getTime()) / MS_PER_DAY;
                    const weeksInMonth = Math.max(5, Math.ceil(days / 7));

                    const rect = el.parentElement.getBoundingClientRect();
                    setCellWidth(Math.ceil(rect.width / 7) - 1);
                    setCellHeight(Math.ceil(rect.height / weeksInMonth) - 1);
                }
            });
        });

        requestAnimationFrame(() => {
            if (el.parentElement) {
                observer.observe(el.parentElement);
            }
        });
    }

    const top = createMemo(() => {
        const dt = ctx.day();
        const sow = startOfWeek(dt);
        const som = startOfMonth(dt);

        const ms = (sow.getTime() - som.getTime()) / 7;
        return Math.ceil(ms / MS_PER_DAY) * cellHeight() + 4
    });

    const left = createMemo(() => {
        const dt = ctx.day();
        const sow = startOfWeek(dt);

        let ms = 0;
        switch (ctx.displayMode()) {
            case 'Day':
                ms = (dt.getTime() - sow.getTime());
                break;
            case 'WorkWeek':
                ms = MS_PER_DAY;
                break;
            case 'Week':
                break;
        }

        return (ms / MS_PER_DAY) * cellWidth() + 2
    });

    const width = createMemo(() => {
        switch (ctx.displayMode()) {
            case 'Day':
                return cellWidth();
            case 'Week':
                return cellWidth() * 7 - 4;
            case 'WorkWeek':
                return cellWidth() * 5 - 4;
        }
    });

    return <Show when={ctx.displayMode() !== 'Month'}>
        <div ref={initElement} class={styles['selected-days']} style={{
            top: `${top()}px`,
            left: `${left()}px`,
            width: `${width()}px`,
            height: `${cellHeight() - 8}px`,
        }}></div>
    </Show>
}

function Week(props: { date: Date; }) {
    const days = createMemo(() => {
        const dt = date.startOfWeek(props.date);
        return [0, 1, 2, 3, 4, 5, 6].map(i => date.addDays(dt, i));
    });

    return <>
        <For each={days()}>{(d) => <Day date={d} />}</For>
    </>;
}

function Day(props: { date: Date; }) {
    const ctx = requireCalendarContext();

    const hasAppointments = createMemo(() => {
        return ctx.getAppointments(props.date).length > 0;
    });

    const inCurrentMonth = createMemo(() => {
        const currMonthDate = startOfMonth(ctx.day());
        const monthDate = startOfMonth(props.date);

        return currMonthDate.getFullYear() === monthDate.getFullYear()
            && currMonthDate.getMonth() === monthDate.getMonth();
    });

    const isToday = createMemo(() => truncateTime(props.date).getTime() === truncateTime(new Date()).getTime());

    function navigate(ev: KeyboardEvent) {
        function moveTo(dt: Date) {
            if (ctx.dayEnabled(dt)) {
                ctx.setHoveredDay(dt);
            }
            ev.preventDefault();
        }

        switch (ev.key) {
            case ' ':
            case 'Enter':
                ctx.setDay(props.date);
                ev.preventDefault();
                break;
            case 'ArrowUp': return moveTo(addDays(props.date, -7));
            case 'ArrowDown': return moveTo(addDays(props.date, 7));
            case 'ArrowLeft': return moveTo(addDays(props.date, -1));
            case 'ArrowRight': return moveTo(addDays(props.date, 1));
        }
    }

    return <div onClick={() => ctx.setDay(props.date)}
        tabindex="0"
        onKeyDown={navigate}
        class={`flexbox vertical middle ${styles.day}`}
        classList={{
            [styles['non-empty']]: hasAppointments(),
            [styles['other-month']]: !inCurrentMonth(),
            [styles.disabled]: !ctx.dayEnabled(props.date),
            [styles.hovered]: ctx.hoveredDay() === props.date
        }}>
        <Show when={isToday()}>
            <Dorito type="info" tooltip="Today" />
        </Show>
        <div style="align-content: center">{props.date.getDate()}</div>
    </div>
}
